/**
 * A4 - the operational half: acknowledgement, escalation, the CAP lifecycle,
 * the editable thresholds and the audit log.
 *
 * ACKNOWLEDGEMENT is recorded, not invented. Whether a District Collector
 * actually confirmed receipt is operational fact this system does not hold, so
 * it is not manufactured here: the operator marks it on this panel and the
 * audit log records who was marked and at what exercise clock. What IS computed
 * is the confirmation path - which of a recipient's channels can carry a reply
 * at all - and the clock at which an unconfirmed dispatch escalates.
 *
 * THRESHOLDS re-evaluate the whole page. Moving a slider re-runs the alert on
 * the same simulation result with the new rule, so the preview is the real
 * answer, not a sketch of one.
 */

import { useState } from 'react'

import { RecipientEditor } from './RecipientEditor'
import { SEVERITY_COLOUR, clock, csvCell, download, istTime, plusMinutes } from './shared'
import type { AckRow, Evaluation, RuleRow } from './shared'
import { C } from '../../design/tokens'

interface Props {
  evaluation: Evaluation
  ruleValues: Record<string, number>
  onRuleChange: (key: string, value: number) => void
  t: number
  confirmed: Record<string, number>
  onConfirm: (recipient: string, tMin: number) => void
  siteId: string | null
}

type Panel = 'acknowledgement' | 'lifecycle' | 'thresholds' | 'recipients'

function state(row: AckRow, t: number, confirmedAt: number | undefined) {
  if (confirmedAt !== undefined) return { label: 'CONFIRMED', colour: C.accent }
  if (t < row.t_notified_min) return { label: 'not yet sent', colour: 'var(--ink-text-faint)' }
  if (t < row.confirmation_due_min) return { label: 'awaiting', colour: 'var(--ink-text-dim)' }
  return { label: 'ESCALATED', colour: C.sevExtreme }
}

export function Operations({
  evaluation,
  ruleValues,
  onRuleChange,
  t,
  confirmed,
  onConfirm,
  siteId,
}: Props) {
  const [panel, setPanel] = useState<Panel>('acknowledgement')
  const tracking = evaluation.acknowledgement

  const exportCsv = () => {
    const header = [
      'exercise_clock',
      'timestamp',
      'recipient',
      'tier',
      'channel',
      'severity',
      'settlement',
      'arrival_min',
      'lead_min',
      'confirmation_path',
      'acknowledged_at_clock',
    ]
    const ack = new Map(tracking.map((r) => [r.recipient, r]))
    const lines = [
      header.join(','),
      ...evaluation.events.map((e) => {
        const at = confirmed[e.recipient]
        return [
          e.exercise_clock,
          e.timestamp,
          e.recipient,
          e.tier,
          e.channel,
          e.severity,
          e.settlement,
          e.arrival_time_min ?? '',
          e.lead_time_min === null ? '' : e.lead_time_min.toFixed(1),
          ack.get(e.recipient)?.confirmation_path ? 'yes' : 'no',
          at === undefined ? '' : clock(at),
        ]
          .map(csvCell)
          .join(',')
      }),
    ]
    download(
      `audit_log_${evaluation.run.run_id}.csv`,
      'text/csv',
      `# run ${evaluation.run.run_id} (${evaluation.run.scenario}) · exercise start ${evaluation.exercise_start}\n` +
        lines.join('\n'),
    )
  }

  const tabs: { key: Panel; label: string }[] = [
    { key: 'acknowledgement', label: `Acknowledgement · ${tracking.length}` },
    { key: 'lifecycle', label: 'Lifecycle' },
    { key: 'thresholds', label: 'Thresholds' },
    { key: 'recipients', label: `Recipients · ${evaluation.directory.length}` },
  ]

  return (
    <section className="panel" style={{ padding: 0 }} data-testid="alert-operations">
      <div className="panel-header flex-wrap items-baseline gap-x-3">
        <span className="panel-title">Operations</span>
        <div className="ml-auto flex gap-1">
          {tabs.map((item) => (
            <button
              key={item.key}
              type="button"
              className="ctl"
              data-active={panel === item.key}
              onClick={() => setPanel(item.key)}
            >
              {item.label}
            </button>
          ))}
          <button type="button" className="ctl" onClick={exportCsv}>
            Audit log CSV
          </button>
        </div>
      </div>

      <div className="px-4 pb-3 pt-2">
        {panel === 'acknowledgement' && (
          <div className="overflow-x-auto">
          <table className="data-table w-full" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th>recipient</th>
                <th className="num">tier</th>
                <th className="num">notified</th>
                <th className="num">confirmation due</th>
                <th>state</th>
                <th>escalates to</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tracking.map((row) => {
                const at = confirmed[row.recipient]
                const s = state(row, t, at)
                return (
                  <tr key={row.recipient}>
                    <td>
                      <div style={{ color: 'var(--ink-text)' }}>{row.recipient}</div>
                      <div
                        style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-faint)' }}
                      >
                        {row.path_note}
                      </div>
                    </td>
                    <td className="num">{row.tier}</td>
                    <td className="num">{clock(row.t_notified_min)}</td>
                    <td className="num">{clock(row.confirmation_due_min)}</td>
                    <td style={{ color: s.colour, fontWeight: 600 }}>
                      {s.label}
                      {at !== undefined && (
                        <span
                          className="font-mono"
                          style={{ color: 'var(--ink-text-faint)', fontWeight: 400 }}
                        >
                          {' '}
                          {clock(at)}
                        </span>
                      )}
                    </td>
                    <td style={{ color: 'var(--ink-text-dim)' }}>
                      {s.label === 'ESCALATED' ? row.escalates_to : ''}
                    </td>
                    <td>
                      {at === undefined && t >= row.t_notified_min && (
                        <button
                          type="button"
                          className="ctl"
                          onClick={() => onConfirm(row.recipient, t)}
                        >
                          Confirm
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </div>
        )}

        {panel === 'lifecycle' && (
          <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th>CAP msgType</th>
                  <th>carries</th>
                  <th>references</th>
                  <th className="num">issued</th>
                </tr>
              </thead>
              <tbody>
                {evaluation.lifecycle.map((step) => (
                  <tr key={step.msg_type}>
                    <td>
                      <span
                        style={{
                          color:
                            step.msg_type === 'Alert'
                              ? SEVERITY_COLOUR.Extreme
                              : step.msg_type === 'Update'
                                ? SEVERITY_COLOUR.Moderate
                                : 'var(--ink-accent)',
                          fontWeight: 600,
                        }}
                      >
                        {step.msg_type}
                      </span>
                    </td>
                    <td style={{ color: 'var(--ink-text-dim)' }}>{step.carries}</td>
                    <td style={{ color: 'var(--ink-text-faint)' }}>{step.references ?? ''}</td>
                    <td className="num">
                      {step.timestamp ? istTime(step.timestamp) : step.state}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div>
              <div className="section-label mb-2">Escalation</div>
              <table className="data-table w-full">
                <tbody>
                  {evaluation.escalation_routes.map((route) => (
                    <tr key={route.tier}>
                      <td style={{ color: 'var(--ink-text-dim)', verticalAlign: 'top' }}>
                        {route.tier_label}
                        {route.recipients && route.recipients.length > 0 && (
                          <div style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-faint)' }}>
                            {route.recipients.length} in tier
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ color: 'var(--ink-text)' }}>{route.escalates_to}</div>
                        <div
                          className="font-mono"
                          style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-faint)' }}
                        >
                          after {route.after_min} min
                        </div>
                        {route.recipients && route.recipients.length > 0 && (
                          <div style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}>
                            {route.recipients.join(' · ')}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {panel === 'recipients' && siteId && <RecipientEditor siteId={siteId} evaluation={evaluation} />}

        {panel === 'thresholds' && (
          <div className="grid gap-3">
            {evaluation.rules
              .filter((rule: RuleRow) => rule.editable)
              .map((rule) => (
                <div key={rule.key} className="flex flex-wrap items-center gap-3">
                  <span style={{ width: 400, fontSize: 'var(--fs-cell)', color: 'var(--ink-text)' }}>
                    {rule.rule}
                  </span>
                  <input
                    type="range"
                    min={rule.min}
                    max={rule.max}
                    step={rule.step}
                    value={ruleValues[rule.key] ?? (rule.value as number)}
                    onChange={(event) => onRuleChange(rule.key, Number(event.target.value))}
                    style={{ width: 220, accentColor: 'var(--ink-accent)' }}
                  />
                  <span
                    className="font-mono tabular-nums"
                    style={{ width: 90, fontSize: 'var(--fs-cell)', color: 'var(--ink-accent)' }}
                  >
                    {ruleValues[rule.key] ?? (rule.value as number)} {rule.unit}
                  </span>
                  <span style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-faint)' }}>
                    {rule.source}
                  </span>
                </div>
              ))}
            <div
              style={{
                borderTop: '1px solid var(--ink-border)',
                paddingTop: 8,
                fontSize: 'var(--fs-label)',
                color: 'var(--ink-text-dim)',
              }}
            >
              First dispatch {plusMinutes(evaluation.exercise_start, ruleValues.decision_delay_min ?? 0)}{' '}
              · every figure above re-evaluates the same simulation result against the rule as it
              stands.
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
